/**
 * EditableTextCell - Componente para célula editável de texto na tabela
 * Padrão: ícone lápis → input inline → salvar/cancelar
 */
import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Check, X, Loader2 } from 'lucide-react';

interface EditableTextCellProps {
  value: string | null;
  onSave: (val: string) => Promise<void>;
  placeholder?: string;
}

export const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  onSave,
  placeholder = '',
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue(value || '');
    setEditing(true);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue(value || '');
    setEditing(false);
  };

  const handleSave = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSaving(true);
    try {
      await onSave(localValue);
      setEditing(false);
    } catch {
      // error toast handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setLocalValue(value || '');
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="flex items-center gap-1 group cursor-pointer"
        onClick={handleStartEdit}
        title="Clique para editar"
      >
        <span className="text-gray-600 dark:text-gray-400 truncate">
          {value || '-'}
        </span>
        <Edit2
          size={13}
          className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={saving}
        placeholder={placeholder}
        className="w-24 h-7 px-1.5 text-sm bg-white dark:bg-gray-800 border border-blue-400 dark:border-blue-500 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50"
      />
      {saving ? (
        <Loader2 size={14} className="animate-spin text-blue-500 flex-shrink-0" />
      ) : (
        <>
          <button
            type="button"
            onClick={handleSave}
            className="p-0.5 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors"
            title="Salvar (Enter)"
          >
            <Check size={14} />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="p-0.5 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            title="Cancelar (Esc)"
          >
            <X size={14} />
          </button>
        </>
      )}
    </div>
  );
};
